/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import i18next from 'i18next';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth-store';
import { api, getSelf } from '@/lib/api';
import { OAuthCallbackScreen } from '@/features/auth/components/oauth-callback-screen';
import { OAUTH_BIND_STORAGE_KEY } from '@/features/auth/constants';
type OAuthRequestConfig = AxiosRequestConfig & {
  skipBusinessError?: boolean;
};
function OAuthCallback() {
  const navigate = useNavigate();
  const {
    provider
  } = useParams({
    from: '/oauth/$provider'
  }) as {
    provider: string;
  };
  const search = useSearch({
    from: '/oauth/$provider'
  }) as {
    code?: string;
    state?: string;
    redirect?: string;
  };
  const [mode, setMode] = useState<'login' | 'bind'>(() => {
    if (typeof window === 'undefined') return 'login';
    return window.opener ? 'bind' : 'login';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMode(window.opener ? 'bind' : 'login');
  }, []);
  useEffect(() => {
    ;
    (async () => {
      const safeNavigate = (target: string) => {
        navigate({
          to: target as never,
          replace: true
        });
        if (typeof window !== 'undefined') {
          setTimeout(() => {
            const normalizedTarget = target.startsWith('/') ? target : `/${target}`;
            const currentPath = window.location.pathname + window.location.search;
            if (currentPath !== normalizedTarget && currentPath !== `${normalizedTarget}/`) {
              window.location.replace(target);
            }
          }, 100);
        }
      };
      if (!search?.code) {
        toast.error(i18next.t('Missing code'));
        safeNavigate('/sign-in');
        return;
      }
      const isBindingFlow = typeof window !== 'undefined' ? Boolean(window.opener) : mode === 'bind';
      if (isBindingFlow && mode !== 'bind') {
        setMode('bind');
      } else if (!isBindingFlow && mode !== 'login') {
        setMode('login');
      }
      const notifyBindingResult = (status: 'success' | 'error') => {
        if (typeof window === 'undefined') return;
        try {
          window.localStorage.setItem(OAUTH_BIND_STORAGE_KEY, JSON.stringify({
            provider,
            status,
            timestamp: Date.now()
          }));
        } catch (_error) {
          // ignore storage write failures
          void _error;
        }
      };
      const closeBindingWindow = () => {
        if (typeof window === 'undefined') return;
        window.close();
        setTimeout(() => {
          if (!window.closed) {
            window.location.replace('/_authenticated/profile/');
          }
        }, 200);
      };
      const finalizeLogin = async (): Promise<boolean> => {
        try {
          const selfResponse = (await getSelf()) as {
            success?: boolean;
            data?: AuthUser | null;
          };
          if (selfResponse?.success && selfResponse.data) {
            useAuthStore.getState().auth.setUser(selfResponse.data);
            try {
              if (typeof window !== 'undefined' && selfResponse.data?.id != null) {
                window.localStorage.setItem('uid', String(selfResponse.data.id));
              }
            } catch (_error) {
              void _error;
            }
            return true;
          }
        } catch (_error) {
          void _error;
        }
        return false;
      };
      const redirectAfterLogin = (target?: string) => {
        const to = target || search?.redirect || '/dashboard';
        safeNavigate(to);
        toast.success(i18next.t('Signed in successfully!'));
      };
      const handleBindingFailure = (message: string) => {
        notifyBindingResult('error');
        toast.error(message);
      };
      const handleLoginFailure = async (message: string) => {
        if (await finalizeLogin()) {
          redirectAfterLogin();
          return;
        }
        toast.error(message);
        safeNavigate('/sign-in');
      };
      try {
        const config: OAuthRequestConfig = {
          params: {
            code: search.code,
            state: search.state
          },
          skipBusinessError: true
        };
        const res = await api.get(`/api/oauth/${provider}`, config);
        if (res?.data?.success) {
          const {
            message
          } = res.data;
          const loginUser = (res.data?.data ?? null) as AuthUser | null;
          // Check if this is a bind operation
          if (message === 'bind') {
            toast.success(i18next.t('Binding successful!'));
            notifyBindingResult('success');
            if (isBindingFlow) {
              // Close the callback window if we opened a new tab for binding
              closeBindingWindow();
            } else {
              safeNavigate('/_authenticated/profile/');
            }
            return;
          }
          // Otherwise it's a login, use payload user if available
          if (loginUser) {
            useAuthStore.getState().auth.setUser(loginUser);
            try {
              if (typeof window !== 'undefined' && loginUser.id != null) {
                window.localStorage.setItem('uid', String(loginUser.id));
              }
            } catch (_error) {
              void _error;
            }
            redirectAfterLogin();
            return;
          }
          if (await finalizeLogin()) {
            redirectAfterLogin();
            return;
          }
          toast.error(res?.data?.message || i18next.t('OAuth failed'));
          safeNavigate('/sign-in');
          return;
        }
        const message = res?.data?.message || 'OAuth failed';
        if (!res?.data?.success && !isBindingFlow) {
          // When logging in with an already bound GitHub account, backend may return this message
          if (message === '该 GitHub 账户已被绑定') {
            if (await finalizeLogin()) {
              redirectAfterLogin();
              return;
            }
          }
        }
        if (isBindingFlow) {
          handleBindingFailure(message);
        } else {
          await handleLoginFailure(message);
        }
        return;
      } catch (error) {
        const message = ((error && typeof error === 'object' && 'response' in error && (error as {
          response?: {
            data?: {
              message?: string;
            };
          };
        }).response?.data?.message) ?? (error instanceof Error ? error.message : undefined) ?? 'OAuth failed') as string;
        if (isBindingFlow) {
          handleBindingFailure(message);
          return;
        }
        await handleLoginFailure(message);
        return;
      }
    })();
  }, [mode, navigate, provider, search]);
  return <OAuthCallbackScreen provider={provider} mode={mode} />;
}
export const Route = createFileRoute('/oauth/$provider')({
  component: OAuthCallback
});
import { performReactRefresh as __tsr_performReactRefresh } from 'react-refresh/runtime';
if (import.meta.webpackHot) {
  const hot = import.meta.webpackHot;
  const hotData = hot.data ??= {};
  const routeId = hotData['tsr-route-id'] ?? Route.id ?? (Route.isRoot ? '__root__' : "/oauth/$provider");
  if (routeId) {
    hotData['tsr-route-id'] = routeId;
  }
  const existingRoute = typeof window !== 'undefined' && routeId ? window.__TSR_ROUTER__?.routesById?.[routeId] : undefined;
  if (routeId && existingRoute && existingRoute !== Route) {
    (function handleRouteUpdate(routeId, newRoute) {
      const router = window.__TSR_ROUTER__;
      const oldRoute = router.routesById[routeId];
      if (!oldRoute) return;
      const removedKeys = new Set();
      Object.keys(oldRoute.options).forEach(key => {
        if (!(key in newRoute.options)) {
          removedKeys.add(key);
          delete oldRoute.options[key];
        }
      });
      const preserveComponentIdentity = "shellComponent" in oldRoute.options === "shellComponent" in newRoute.options;
      const componentKeys = ["component", "shellComponent", "pendingComponent", "errorComponent", "notFoundComponent"];
      if (preserveComponentIdentity) componentKeys.forEach(key => {
        if (key in oldRoute.options && key in newRoute.options) newRoute.options[key] = oldRoute.options[key];
      });
      oldRoute.options = newRoute.options;
      oldRoute.update(newRoute.options);
      oldRoute._componentsPromise = void 0;
      oldRoute._lazyPromise = void 0;
      router.routesById[oldRoute.id] = oldRoute;
      router.routesByPath[oldRoute.fullPath] = oldRoute;
      router.processedTree.matchCache.clear();
      router.processedTree.flatCache?.clear();
      router.processedTree.singleCache.clear();
      router.resolvePathCache.clear();
      walkReplaceSegmentTree(oldRoute, router.processedTree.segmentTree);
      const filter = m => m.routeId === oldRoute.id;
      const activeMatch = router.stores.matches.get().find(filter);
      const pendingMatch = router.stores.pendingMatches.get().find(filter);
      const cachedMatches = router.stores.cachedMatches.get().filter(filter);
      if (activeMatch || pendingMatch || cachedMatches.length > 0) {
        if (removedKeys.has("loader") || removedKeys.has("beforeLoad")) {
          const matchIds = [activeMatch?.id, pendingMatch?.id, ...cachedMatches.map(match => match.id)].filter(Boolean);
          router.batch(() => {
            for (const matchId of matchIds) {
              const store = router.stores.pendingMatchStores.get(matchId) || router.stores.matchStores.get(matchId) || router.stores.cachedMatchStores.get(matchId);
              if (store) store.set(prev => {
                const next = {
                  ...prev
                };
                if (removedKeys.has("loader")) next.loaderData = void 0;
                if (removedKeys.has("beforeLoad")) {
                  next.__beforeLoadContext = void 0;
                  next.context = rebuildMatchContextWithoutBeforeLoad(next);
                }
                return next;
              });
            }
          });
        }
        router.invalidate({
          filter,
          sync: true
        });
      }
      function walkReplaceSegmentTree(route, node) {
        if (node.route?.id === route.id) node.route = route;
        if (node.index) walkReplaceSegmentTree(route, node.index);
        node.static?.forEach(child => walkReplaceSegmentTree(route, child));
        node.staticInsensitive?.forEach(child => walkReplaceSegmentTree(route, child));
        node.dynamic?.forEach(child => walkReplaceSegmentTree(route, child));
        node.optional?.forEach(child => walkReplaceSegmentTree(route, child));
        node.wildcard?.forEach(child => walkReplaceSegmentTree(route, child));
      }
      function getStoreMatch(matchId) {
        return router.stores.pendingMatchStores.get(matchId)?.get() || router.stores.matchStores.get(matchId)?.get() || router.stores.cachedMatchStores.get(matchId)?.get();
      }
      function getMatchList(matchId) {
        const pendingMatches = router.stores.pendingMatches.get();
        if (pendingMatches.some(match => match.id === matchId)) return pendingMatches;
        const activeMatches = router.stores.matches.get();
        if (activeMatches.some(match => match.id === matchId)) return activeMatches;
        const cachedMatches = router.stores.cachedMatches.get();
        if (cachedMatches.some(match => match.id === matchId)) return cachedMatches;
        return [];
      }
      function getParentMatch(match) {
        const matchList = getMatchList(match.id);
        const matchIndex = matchList.findIndex(item => item.id === match.id);
        if (matchIndex <= 0) return;
        const parentMatch = matchList[matchIndex - 1];
        return getStoreMatch(parentMatch.id) || parentMatch;
      }
      function rebuildMatchContextWithoutBeforeLoad(match) {
        const parentMatch = getParentMatch(match);
        const getParentContext = router.getParentContext;
        return {
          ...((getParentContext ? getParentContext.call(router, parentMatch) : parentMatch?.context ?? router.options.context) ?? {}),
          ...(match.__routeContext ?? {})
        };
      }
    })(routeId, Route);
    const tsrRefreshState = globalThis.__TSR_HMR__ ??= {};
    try {
      if (!tsrRefreshState.refreshScheduled) {
        tsrRefreshState.refreshScheduled = true;
        setTimeout(() => {
          tsrRefreshState.refreshScheduled = false;
          try {
            __tsr_performReactRefresh();
          } catch (_e) {}
        }, 30);
      }
    } catch (_err) {}
  }
  hot.dispose(data => {
    if (routeId) {
      data['tsr-route-id'] = routeId;
    }
  });
  hot.accept();
}